import "@/App.css";
import { Suspense, lazy, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

const DashboardLayout = lazy(() => import("@/components/DashboardLayout"));
const ChatWidget = lazy(() => import("@/components/ChatWidget"));
const LazyToaster = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));

// Defer chat widget + toaster until the browser is idle so they never compete with first paint.
const DeferredExtras = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(() => setReady(true), { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(() => setReady(true), 2500);
    return () => clearTimeout(t);
  }, []);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <ChatWidget />
      <LazyToaster position="top-center" richColors />
    </Suspense>
  );
};

// Marketing entry pages stay eager for instant paint; deeper marketing pages are
// code-split so their data files (blog/cities/guides/vs) stay out of the main bundle.
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import NotFound from "@/pages/NotFound";

const Login = lazy(() => import("@/pages/Login"));
const Signup = lazy(() => import("@/pages/Signup"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Contact = lazy(() => import("@/pages/Contact"));
const Vs = lazy(() => import("@/pages/Vs"));
const Compare = lazy(() => import("@/pages/Compare"));
const Guides = lazy(() => import("@/pages/Guides"));
const Guide = lazy(() => import("@/pages/Guide"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const Security = lazy(() => import("@/pages/Security"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const ShareWin = lazy(() => import("@/pages/ShareWin"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const City = lazy(() => import("@/pages/City"));
const ServiceAreas = lazy(() => import("@/pages/ServiceAreas"));

// App + heavy pages (jsPDF, canvas, QR) are code-split so marketing pages load fast.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const NewEstimate = lazy(() => import("@/pages/NewEstimate"));
const Customers = lazy(() => import("@/pages/Customers"));
const Leads = lazy(() => import("@/pages/Leads"));
const Signups = lazy(() => import("@/pages/Signups"));
const Outreach = lazy(() => import("@/pages/Outreach"));
const Templates = lazy(() => import("@/pages/Templates"));
const Settings = lazy(() => import("@/pages/Settings"));
const Team = lazy(() => import("@/pages/Team"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const SignEstimate = lazy(() => import("@/pages/SignEstimate"));
const Portal = lazy(() => import("@/pages/Portal"));
const Calendar = lazy(() => import("@/pages/Calendar"));
const Invoice = lazy(() => import("@/pages/Invoice"));
const Rate = lazy(() => import("@/pages/Rate"));
const Refer = lazy(() => import("@/pages/Refer"));

const PageSpinner = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-500" />
  </div>
);

const Dash = ({ children }) => (
  <ProtectedRoute>
    <DashboardLayout>{children}</DashboardLayout>
  </ProtectedRoute>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<PageSpinner />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/register" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/vs" element={<Compare />} />
          <Route path="/vs/:slug" element={<Vs />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/guides/:slug" element={<Guide />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/security" element={<Security />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/sign/:shareId" element={<SignEstimate />} />
          <Route path="/invoice/:shareId" element={<Invoice />} />
          <Route path="/rate/:shareId" element={<Rate />} />
          <Route path="/refer/:shareId" element={<Refer />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/hvac-estimating-software/:city" element={<City />} />
          <Route path="/service-areas" element={<ServiceAreas />} />
          <Route path="/portal/:portalId" element={<Portal />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/share-a-win" element={<ShareWin />} />
          <Route path="/dashboard" element={<Dash><Dashboard /></Dash>} />
          <Route path="/dashboard/new" element={<Dash><NewEstimate /></Dash>} />
          <Route path="/dashboard/customers" element={<Dash><Customers /></Dash>} />
          <Route path="/dashboard/calendar" element={<Dash><Calendar /></Dash>} />
          <Route path="/dashboard/leads" element={<Dash><Leads /></Dash>} />
          <Route path="/dashboard/signups" element={<Dash><Signups /></Dash>} />
          <Route path="/dashboard/outreach" element={<Dash><Outreach /></Dash>} />
          <Route path="/dashboard/templates" element={<Dash><Templates /></Dash>} />
          <Route path="/dashboard/team" element={<Dash><Team /></Dash>} />
          <Route path="/dashboard/settings" element={<Dash><Settings /></Dash>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        <DeferredExtras />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
